import { Row, Col, Slider, InputNumber } from "antd";
import React, { ChangeEvent } from "react";
import { LayerTypeEnum } from "../../Enums/GPSMapEnum";
import { rgbToHex, hexToRgb } from "../../Util/ColorUtil";

interface MapLayerProps {
  isHome: boolean;
  layerType: LayerTypeEnum;
  scatterplotOpacity: number;
  scatterplotRadius: number;
  scatterplotLineWidth: number;
  scatterplotHomeColor: any[];
  scatterplotWorkColor: any[];
  setScatterplotOpacity: (value: number) => void;
  setScatterplotRadius: (value: number) => void;
  setScatterplotLineWidth: (value: number) => void;
  setScatterplotHomeColor: (value: any[]) => void;
  setScatterplotWorkColor: (value: any[]) => void;

  heatmapOpacity: number;
  heatmapRadius: number;
  heatmapIntensity: number;
  heatmapThreshold: number;
  setHeatmapRadius: (value: number) => void;
  setHeatmapOpacity: (value: number) => void;
  setHeatmapThreshold: (value: number) => void;
  setHeatmapIntensity: (value: number) => void;

  hexOpacity: number;
  hexRadius: number;
  hexLowerPercentile: number;
  hexUpperPercentile: number;
  hexCoverage: number;
  setHexOpacity: (value: number) => void;
  setHexRadius: (value: number) => void;
  setHexLowerPercentile: (value: number) => void;
  setHexCoverage: (value: number) => void;
  setHexUpperPercentile: (value: number) => void;
}

export const MapLayerOptions = (props: MapLayerProps) => {
    const { layerType } = props;

    const handleScatterplotHomeColorChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const { r: red, g: green, b: blue } = hexToRgb(event.target.value);
        props.setScatterplotHomeColor([red, green, blue]);
    };

    const handleScatterplotWorkColorChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const { r: red, g: green, b: blue } = hexToRgb(event.target.value);
        props.setScatterplotWorkColor([red, green, blue]);
    };

    switch (layerType) {
        case LayerTypeEnum.Scatterplot:
            return (
                <>
                    <div>Opacity</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={0.01}
                                max={1}
                                step={0.01}
                                value={
                                    typeof props.scatterplotOpacity === "number"
                                        ? props.scatterplotOpacity
                                        : 0
                                }
                                onChange={(value) =>
                                    props.setScatterplotOpacity(value as number)
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={0.01}
                                max={1}
                                step={0.01}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.scatterplotOpacity}
                                onChange={(value) =>
                                    props.setScatterplotOpacity(value as number)
                                }
                            />
                        </Col>
                    </Row>
                    <div>Radius</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={2}
                                max={100}
                                step={1}
                                value={
                                    typeof props.scatterplotRadius === "number"
                                        ? props.scatterplotRadius
                                        : 0
                                }
                                onChange={(value) =>
                                    props.setScatterplotRadius(value as number)
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={2}
                                max={100}
                                step={1}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.scatterplotRadius}
                                onChange={(value) =>
                                    props.setScatterplotRadius(value as number)
                                }
                            />
                        </Col>
                    </Row>
                    <div>Line Width</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={1}
                                max={10}
                                step={0.5}
                                value={
                                    typeof props.scatterplotLineWidth === "number"
                                        ? props.scatterplotLineWidth
                                        : 0
                                }
                                onChange={(value) =>
                                    props.setScatterplotLineWidth(value as number)
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={1}
                                max={10}
                                step={0.5}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.scatterplotLineWidth}
                                onChange={(value) =>
                                    props.setScatterplotLineWidth(value as number)
                                }
                            />
                        </Col>
                    </Row>
                    {props.isHome ? (
                        <>
                            <div>Color</div>
                            <Row style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Col span={10}>
                                    <input
                                        style={{ width: "100%" }}
                                        type="color"
                                        value={rgbToHex(
                                            props.scatterplotHomeColor[0],
                                            props.scatterplotHomeColor[1],
                                            props.scatterplotHomeColor[2]
                                        )}
                                        onChange={handleScatterplotHomeColorChange}
                                    />
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <div>Color</div>
                            <Row style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Col span={10}>
                                    <input
                                        style={{ width: "100%" }}
                                        type="color"
                                        value={rgbToHex(
                                            props.scatterplotWorkColor[0],
                                            props.scatterplotWorkColor[1],
                                            props.scatterplotWorkColor[2]
                                        )}
                                        onChange={handleScatterplotWorkColorChange}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            );

        case LayerTypeEnum.Heatmap:
            return (
                <>
                    <div>Opacity</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={0.01}
                                max={1}
                                step={0.01}
                                value={
                                    typeof props.heatmapOpacity === "number"
                                        ? props.heatmapOpacity
                                        : 0
                                }
                                onChange={(value) => props.setHeatmapOpacity(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={0.01}
                                max={1}
                                step={0.01}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.heatmapOpacity}
                                onChange={(value) => props.setHeatmapOpacity(value as number)}
                            />
                        </Col>
                    </Row>
                    <div>Radius</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={1}
                                max={100}
                                step={1}
                                value={
                                    typeof props.heatmapRadius === "number"
                                        ? props.heatmapRadius
                                        : 0
                                }
                                onChange={(value) => props.setHeatmapRadius(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={1}
                                max={100}
                                step={1}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.heatmapRadius}
                                onChange={(value) => props.setHeatmapRadius(value as number)}
                            />
                        </Col>
                    </Row>
                    <div>Intensity</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={1}
                                max={100}
                                step={1}
                                value={
                                    typeof props.heatmapIntensity === "number"
                                        ? props.heatmapIntensity
                                        : 0
                                }
                                onChange={(value) => props.setHeatmapIntensity(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={1}
                                max={100}
                                step={1}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.heatmapIntensity}
                                onChange={(value) => props.setHeatmapIntensity(value as number)}
                            />
                        </Col>
                    </Row>
                    <div>Threshold</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={0.01}
                                max={1}
                                step={0.01}
                                value={
                                    typeof props.heatmapThreshold === "number"
                                        ? props.heatmapThreshold
                                        : 0
                                }
                                onChange={(value) => props.setHeatmapThreshold(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={0.01}
                                max={1}
                                step={0.01}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.heatmapThreshold}
                                onChange={(value) => props.setHeatmapThreshold(value as number)}
                            />
                        </Col>
                    </Row>
                </>
            );

        case LayerTypeEnum.Hexagon:
            return (
                <>
                    <div>Opacity</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={0.01}
                                max={1}
                                step={0.01}
                                value={
                                    typeof props.hexOpacity === "number" ? props.hexOpacity : 0
                                }
                                onChange={(value) => props.setHexOpacity(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={0.01}
                                max={1}
                                step={0.01}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.hexOpacity}
                                onChange={(value) => props.setHexOpacity(value as number)}
                            />
                        </Col>
                    </Row>
                    <div>Radius</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={10}
                                max={1000}
                                step={10}
                                value={
                                    typeof props.hexRadius === "number" ? props.hexRadius : 0
                                }
                                onChange={(value) => props.setHexRadius(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={10}
                                max={1000}
                                step={10}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.hexRadius}
                                onChange={(value) => props.setHexRadius(value as number)}
                            />
                        </Col>
                    </Row>
                    <div>Lower Percentile</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={1}
                                max={100}
                                step={1}
                                value={
                                    typeof props.hexLowerPercentile === "number"
                                        ? props.hexLowerPercentile
                                        : 0
                                }
                                onChange={(value) =>
                                    props.setHexLowerPercentile(value as number)
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={1}
                                max={100}
                                step={1}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.hexLowerPercentile}
                                onChange={(value) =>
                                    props.setHexLowerPercentile(value as number)
                                }
                            />
                        </Col>
                    </Row>
                    <div>Upper Percentile</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={1}
                                max={100}
                                step={1}
                                value={
                                    typeof props.hexUpperPercentile === "number"
                                        ? props.hexUpperPercentile
                                        : 0
                                }
                                onChange={(value) =>
                                    props.setHexUpperPercentile(value as number)
                                }
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={1}
                                max={100}
                                step={1}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.hexUpperPercentile}
                                onChange={(value) =>
                                    props.setHexUpperPercentile(value as number)
                                }
                            />
                        </Col>
                    </Row>
                    <div>Coverage</div>
                    <Row style={{ display: "flex", alignItems: "center" }}>
                        <Col span={14}>
                            <Slider
                                min={0.01}
                                max={1}
                                step={0.01}
                                value={
                                    typeof props.hexCoverage === "number" ? props.hexCoverage : 0
                                }
                                onChange={(value) => props.setHexCoverage(value as number)}
                            />
                        </Col>
                        <Col span={10}>
                            <InputNumber
                                min={0.01}
                                max={1}
                                step={0.01}
                                style={{ margin: "0 16px", width: "100%" }}
                                value={props.hexCoverage}
                                onChange={(value) => props.setHexCoverage(value as number)}
                            />
                        </Col>
                    </Row>
                </>
            );

        default:
            return null;
    }
};
